<template>
    <text-input
        ref="input"
        class="date-input"
        autocomplete="off"
        v-model="formattedValue"
        :name="name"
        :placeholder="placeholder"
        :error="error"
        :disabled="disabled"
        @click="onClick" />
</template>

<script>
import TextInput from './TextInput';
import { mapGetters } from 'vuex';
import { formattedToIso, isoToFormatted } from '../helpers/DateHelper';
import { calculateModalPosition } from '../helpers/ModalHelper';
import { uuid } from '../helpers/GeneralHelper';
import moment from 'moment';

export default {
    props: {
        type: {
            type: String,
            default: 'date',
        },
        name: String,
        value: [String, Date],
        placeholder: String,
        error: [Boolean, String], // Boolean to just show add the class, String to also display a message
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            uuid: null,
            formattedValue: null,
        };
    },
    computed: {
        ...mapGetters({
            modalKey: 'dateModal/getKey',
            modalValue: 'dateModal/getValue',
        }),
        modalProps() {
            if (this.type === 'datetime') {
                return { pickTime: true };
            }

            return {};
        },
        format() {
            if (this.type === 'datetime') {
                return 'DD/MM/YYYY HH:mm';
            }

            return 'DD/MM/YYYY';
        },
        key() {
            return this.uuid;
        },
    },
    methods: {
        onValueChanged(value) {
            // When nobody is listening on the input just update the internal value
            if (this.$listeners && this.$listeners.input) {
                this.$emit('input', value);
            } else {
                this.value = value;
            }
        },
        onClick(event) {
            // Propagate click event
            this.$emit('click', event);
        },
        onFocusChanged(event) {
            // Check if input disabled
            if (this.disabled) {
                return;
            }

            // Open modal for this input when needed
            const activeElement = event.target;
            if (
                this.isInputActive(activeElement) &&
                this.modalKey !== this.key
            ) {
                // Open modal
                this.openModal();
            }

            // Check if modal should be closed
            if (
                this.modalKey === this.key &&
                !this.isInputActive(activeElement) &&
                !this.isModalActive(activeElement)
            ) {
                // Close modal
                this.closeModal();
            }
        },
        isInputActive(activeElement) {
            // Check if input is focused
            const inputElement = this.$refs.input
                ? this.$refs.input.$refs.input
                : null;
            return (
                inputElement &&
                (inputElement === activeElement ||
                    inputElement.contains(activeElement))
            );
        },
        isModalActive(activeElement) {
            const modalElement = document.getElementById('date-modal');
            return (
                modalElement &&
                (modalElement === activeElement ||
                    modalElement.contains(activeElement))
            );
        },
        openModal() {
            // Open modal
            this.$store.dispatch('dateModal/open', {
                key: this.key,
                value: this.value,
                props: this.modalProps,
            });

            // Calculate position
            this.$nextTick(this.updateModalPosition);
        },
        updateModalPosition() {
            // Find elements
            const inputElement = this.$refs.input.$refs.input;
            const modalElement = document.getElementById('date-modal');

            // Calculate position
            const position = calculateModalPosition(inputElement, modalElement);

            // Set position
            this.$store.commit('dateModal/setPosition', position);
        },
        closeModal() {
            this.$store.dispatch('dateModal/close');
        },
    },
    mounted() {
        this.uuid = uuid('date');

        // Listen for last clicked element to determine whether to open or close the modal
        document.addEventListener('click', this.onFocusChanged, true);
    },
    destroyed() {
        // Remove listener
        document.removeEventListener('click', this.onFocusChanged, true);
    },
    watch: {
        value: {
            immediate: true,
            handler(value, oldValue) {
                // Take a copy of the value and parse so we can modify it locally
                const formattedValue = isoToFormatted(value, this.format);

                // Fix the special where you type the first character and the value becomes null,
                // so you do not want to set the formatted value back to null
                if (
                    value === null &&
                    oldValue !== null &&
                    this.formattedValue &&
                    this.formattedValue.length === 1
                ) {
                    return;
                }

                // Only update when the value has changed
                if (this.formattedValue !== formattedValue) {
                    this.formattedValue = formattedValue;

                    // Set value of modal
                    this.$store.commit('dateModal/setValue', value);
                }
            },
        },
        formattedValue: {
            handler(formattedValue) {
                // When the local formattedValue changes also update the value with the parsed value
                const value = formattedToIso(formattedValue, this.format);
                if (this.value !== value) {
                    this.onValueChanged(value);

                    // Set value of modal
                    this.$store.commit('dateModal/setValue', value);
                }
            },
        },
        modalValue: {
            handler(modalValue) {
                // Only update the value if the modal is linked to this input
                if (this.modalKey === this.key && modalValue !== this.value) {
                    this.onValueChanged(modalValue);

                    const isDifferentDate =
                        moment.utc(modalValue).format('DD/MM/YYYY') !==
                        moment.utc(this.value).format('DD/MM/YYYY');
                    const isDatetimeDatePick =
                        this.type === 'datetime' && this.value === null;

                    // Only close the model if the date changes (A date is clicked)
                    // And when it is not the first date that is picked when type is datetime
                    // (Because the time picker is only visible after picking a date)
                    if (isDifferentDate && !isDatetimeDatePick) {
                        // Close modal
                        this.closeModal();
                    }
                }
            },
        },
    },
    components: {
        TextInput,
    },
};
</script>
